import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Tabs, 
  Select, 
  Button, 
  Space, 
  Typography, 
  Tag, 
  Timeline, 
  Modal, 
  Form, 
  Input, 
  Row, 
  Col, 
  Alert,
  Tooltip,
  message,
  Descriptions,
  Popconfirm
} from 'antd';
import { 
  CodeOutlined, 
  SaveOutlined, 
  ReloadOutlined, 
  HistoryOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  EditOutlined,
  EyeOutlined
} from '@ant-design/icons';
import CodeMirror from '@uiw/react-codemirror';
import { python } from '@codemirror/lang-python';
import { oneDark } from '@codemirror/theme-one-dark';
import api from '../services/api';

const { Title, Paragraph, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;
const { TextArea } = Input;

// Sample module template
const MODULE_TEMPLATE = `from modules.base_module import BaseModule
from typing import Dict, List, Any

class {className}Module(BaseModule):
    """
    {description}
    """
    
    @property
    def module_info(self) -> Dict[str, Any]:
        return {
            "name": "{moduleName}",
            "type": "processing",
            "description": "{description}",
            "icon": "⚡",
            "category": "custom",
            "version": "1.0.0",
            "fields": [
                {
                    "name": "operation",
                    "label": "Operation Type",
                    "field_type": "select",
                    "required": True,
                    "default_value": "process",
                    "options": {
                        "process": "Process Data",
                        "transform": "Transform Data",
                        "validate": "Validate Data"
                    },
                    "help_text": "Type of operation to perform",
                    "order_index": 1
                },
                {
                    "name": "parameter",
                    "label": "Parameter",
                    "field_type": "string",
                    "required": False,
                    "placeholder": "Enter parameter value",
                    "help_text": "Optional parameter for the operation",
                    "order_index": 2
                }
            ]
        }
    
    def execute(self, settings: Dict[str, Any], trigger_data: Dict[str, Any], 
                execution_log: List[Dict[str, Any]]) -> Dict[str, Any]:
        """Execute the module logic"""
        
        operation = settings.get('operation', 'process')
        parameter = settings.get('parameter', '')
        
        self.log_info(f"Executing {operation} operation")
        
        # Your custom logic here
        result = {
            'operation_performed': operation,
            'parameter_used': parameter,
            'message': f'{operation.title()} operation completed successfully',
            'processed_at': self.get_current_timestamp(),
            'data': trigger_data  # Pass through trigger data
        }
        
        # Pass through session information
        if 'session_id' in trigger_data:
            result['session_id'] = trigger_data['session_id']
        
        return result
`;

const DynamicModuleEditor = () => {
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [moduleCode, setModuleCode] = useState('');
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('code');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [form] = Form.useForm();

  // Load dynamic modules on component mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadDynamicModules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load selected module data when module changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selectedModule) {
      loadModuleData(selectedModule);
      loadModuleVersions(selectedModule);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModule]);

  const loadDynamicModules = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/dynamic-modules');
      const data = response.data;
      setModules(data);
      
      if (data.length > 0 && !selectedModule) {
        setSelectedModule(data[0].name);
      }
    } catch (error) {
      message.error('Failed to load dynamic modules');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadModuleData = async (moduleName) => {
    try {
      const response = await api.get(`/api/admin/dynamic-modules/${encodeURIComponent(moduleName)}`);
      const data = response.data;
      setModuleCode(data.module_code || '');
      form.setFieldsValue({
        description: data.description || '',
        change_log: ''
      });
    } catch (error) {
      console.error('Error loading module data:', error);
    }
  };

  const loadModuleVersions = async (moduleName) => {
    try {
      const response = await api.get(`/api/admin/dynamic-modules/${encodeURIComponent(moduleName)}/versions`);
      const data = response.data;
      setVersions(data);
    } catch (error) {
      console.error('Error loading module versions:', error);
    }
  };

  const saveModule = async (values) => {
    if (!selectedModule) {
      message.error('No module selected');
      return;
    }

    setSaving(true);
    try {
      const response = await api.post(`/api/admin/dynamic-modules/${encodeURIComponent(selectedModule)}`, {
        module_code: moduleCode,
        description: values.description || '',
        change_log: values.change_log || ''
      });

      const data = response.data;
      message.success(`Module saved as version ${data.version_number}! Engine reloaded.`);
      
      // Reload versions and modules
      await Promise.all([
        loadModuleVersions(selectedModule),
        loadDynamicModules()
      ]);
      
      // Clear change log
      form.setFieldsValue({ change_log: '' });
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Error saving module';
      message.error(`Failed to save module: ${errorMessage}`);
      console.error('Error:', error);
    } finally {
      setSaving(false);
    }
  };

  const activateVersion = async (versionNumber) => {
    if (!selectedModule) return;

    try {
      const response = await fetch(
        `/api/admin/dynamic-modules/${encodeURIComponent(selectedModule)}/activate/${versionNumber}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        message.success(`Version ${versionNumber} activated! Engine reloaded.`);
        await Promise.all([
          loadModuleVersions(selectedModule),
          loadDynamicModules(),
          loadModuleData(selectedModule)
        ]);
      } else {
        const error = await response.json();
        message.error(`Failed to activate version: ${error.error}`);
      }
    } catch (error) {
      message.error('Error activating version');
      console.error('Error:', error);
    }
  };

  const viewVersion = async (versionNumber) => {
    try {
      const response = await fetch(
        `/api/admin/dynamic-modules/${encodeURIComponent(selectedModule)}/versions/${versionNumber}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setModuleCode(data.module_code);
        setActiveTab('code');
        message.info(`Viewing version ${versionNumber} (read-only)`);
      }
    } catch (error) {
      console.error('Error viewing version:', error);
    }
  };

  const createNewModule = async () => {
    try {
      const values = await form.validateFields(['newModuleName', 'newModuleDescription']);
      const moduleName = values.newModuleName;
      const description = values.newModuleDescription;
      
      console.log('Creating module:', { moduleName, description });
      
      // First create the module in the database
      const response = await api.post('/api/admin/dynamic-modules', {
        name: moduleName,
        category: 'custom',
        icon: '🔧',
        description: description
      });

      console.log('Response status:', response.status);
      console.log('Response data:', response.data);

      // Generate template code
      const className = moduleName.replace(/\s+/g, '');
      const newCode = MODULE_TEMPLATE
        .replace(/{className}/g, className)
        .replace(/{moduleName}/g, moduleName)
        .replace(/{description}/g, description);
      
      // Reload modules list
      await loadDynamicModules();
      
      // Select the new module
      setSelectedModule(moduleName);
      setModuleCode(newCode);
      setActiveTab('code');
      setIsModalOpen(false);
      form.resetFields();
      
      message.success(`Module "${moduleName}" created successfully! Edit the code and save to create your first version.`);
    } catch (error) {
      if (error.errorFields) {
        message.error('Please fill in all required fields');
      } else {
        const errorMessage = error.response?.data?.error || error.message || 'Error creating module';
        message.error(`Failed to create module: ${errorMessage}`);
        console.error('Error creating module:', error);
      }
    }
  };

  const currentModule = modules.find(m => m.name === selectedModule);

  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card>
            <Space size="large" style={{ width: '100%', justifyContent: 'space-between' }}>
              <div>
                <Title level={2}>
                  <CodeOutlined /> Dynamic Module Editor
                </Title>
                <Paragraph>
                  Edit module code with Python syntax highlighting and version control.
                  Changes are hot-reloaded into the workflow engine.
                </Paragraph>
              </div>
              <Space>
                <Button 
                  type="primary" 
                  icon={<EditOutlined />}
                  onClick={() => setIsModalOpen(true)}
                >
                  New Module
                </Button>
                <Tooltip title="Toggle dark mode">
                  <Button 
                    icon={isDarkMode ? '☀️' : '🌙'}
                    onClick={() => setIsDarkMode(!isDarkMode)}
                  />
                </Tooltip>
              </Space>
            </Space>
          </Card>
        </Col>

        <Col span={6}>
          <Card title="Modules" loading={loading}>
            <Select
              style={{ width: '100%', marginBottom: 16 }}
              placeholder="Select a module"
              value={selectedModule}
              onChange={setSelectedModule}
            >
              {modules.map(module => (
                <Option key={module.name} value={module.name}>
                  <Space>
                    <Text strong>{module.name}</Text>
                    <Tag color={module.is_active ? 'green' : 'default'}>
                      v{module.current_version}
                    </Tag>
                  </Space>
                </Option>
              ))}
            </Select>

            {currentModule && (
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Current Version">
                  <Tag color="blue">v{currentModule.current_version}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Latest Version">
                  <Tag>v{currentModule.latest_version}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Total Versions">
                  {currentModule.total_versions}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={currentModule.is_active ? 'green' : 'red'}>
                    {currentModule.is_active ? 'Active' : 'Inactive'}
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
            )}
          </Card>
        </Col>

        <Col span={18}>
          <Card>
            <Tabs activeKey={activeTab} onChange={setActiveTab}>
              <TabPane tab={<span><CodeOutlined />Code Editor</span>} key="code">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Space>
                    <Text strong>Module: {selectedModule}</Text>
                    {currentModule && (
                      <>
                        <Tag color="blue">Current: v{currentModule.current_version}</Tag>
                        {currentModule.is_built_in && (
                          <Tag color="orange">Built-in Module</Tag>
                        )}
                      </>
                    )}
                  </Space>

                  {currentModule && currentModule.is_built_in ? (
                    <Alert
                      message="Built-in Module"
                      description="This is a built-in module and cannot be edited. Create a new custom module instead."
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                  ) : null}

                  <div style={{ border: '1px solid #d9d9d9', borderRadius: '6px' }}>
                    <CodeMirror
                      value={moduleCode}
                      height="500px"
                      theme={isDarkMode ? oneDark : 'light'}
                      extensions={[python()]}
                      onChange={(value) => setModuleCode(value)}
                      editable={!currentModule?.is_built_in}
                      basicSetup={{
                        lineNumbers: true,
                        foldGutter: true,
                        dropCursor: false,
                        allowMultipleSelections: false,
                        indentOnInput: true,
                        bracketMatching: true,
                        closeBrackets: true,
                        autocompletion: true,
                        highlightSelectionMatches: true,
                        history: true,
                        searchKeymap: true,
                      }}
                    />
                  </div>

                  {!currentModule?.is_built_in && (
                    <Form form={form} onFinish={saveModule} layout="vertical">
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item name="description" label="Description">
                            <Input placeholder="Brief description of changes" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="change_log" label="Change Log" rules={[{ required: true }]}>
                            <Input placeholder="What changed in this version?" />
                          </Form.Item>
                        </Col>
                      </Row>
                      
                      <Space>
                        <Button 
                          type="primary" 
                          htmlType="submit" 
                          icon={<SaveOutlined />}
                          loading={saving}
                          disabled={!moduleCode.trim()}
                        >
                          Save New Version
                        </Button>
                        <Button 
                          icon={<ReloadOutlined />}
                          onClick={() => loadModuleData(selectedModule)}
                        >
                          Reload Current
                        </Button>
                      </Space>
                    </Form>
                  )}
                </Space>
              </TabPane>

              <TabPane tab={<span><HistoryOutlined />Version History</span>} key="versions">
                <Timeline>
                  {versions.map(version => (
                    <Timeline.Item
                      key={version.id}
                      dot={version.is_current ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <ClockCircleOutlined />}
                      color={version.is_current ? 'green' : 'gray'}
                    >
                      <Space direction="vertical" size="small">
                        <Space>
                          <Text strong>Version {version.version_number}</Text>
                          {version.is_current && <Tag color="green">Current</Tag>}
                          {version.is_active && <Tag color="blue">Active</Tag>}
                        </Space>
                        
                        <Text type="secondary">{version.description}</Text>
                        
                        {version.change_log && (
                          <Text italic>"{version.change_log}"</Text>
                        )}
                        
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {new Date(version.created_at).toLocaleString()}
                        </Text>
                        
                        <Space>
                          <Button 
                            size="small" 
                            icon={<EyeOutlined />}
                            onClick={() => viewVersion(version.version_number)}
                          >
                            View Code
                          </Button>
                          
                          {!version.is_current && (
                            <Popconfirm
                              title="Activate this version?"
                              description="This will make this version the current active version and reload the engine."
                              onConfirm={() => activateVersion(version.version_number)}
                              okText="Yes"
                              cancelText="No"
                            >
                              <Button 
                                size="small" 
                                type="primary"
                                icon={<PlayCircleOutlined />}
                              >
                                Activate
                              </Button>
                            </Popconfirm>
                          )}
                        </Space>
                      </Space>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </TabPane>
            </Tabs>
          </Card>
        </Col>
      </Row>

      {/* New Module Modal */}
      <Modal
        title="Create New Module"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={createNewModule}
        okText="Create Template"
      >
        <Form form={form} layout="vertical">
          <Form.Item 
            name="newModuleName" 
            label="Module Name"
            rules={[{ required: true }]}
          >
            <Input placeholder="e.g., Custom Processor" />
          </Form.Item>
          
          <Form.Item 
            name="newModuleDescription" 
            label="Description"
            rules={[{ required: true }]}
          >
            <TextArea 
              rows={3} 
              placeholder="Brief description of what this module does" 
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DynamicModuleEditor;
